import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Button,
    Card,
    Col,
    Empty,
    Row,
    Select,
    Spin,
    Statistic,
    Tag,
    Typography,
    message,
} from "antd";
import {
    DownloadOutlined,
    ReloadOutlined,
    TeamOutlined,
    ApartmentOutlined,
    FilePdfOutlined,
} from "@ant-design/icons";
import { pdf } from "@react-pdf/renderer";

import api from "../../api/api";
import { useAcademicYear } from "../../AcademicYearContext";
import TeacherBioDataDivisionPDF from "./TeacherBioDataDivisionPDF";

const { Title, Text } = Typography;

const getArray = (data) => {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.results)) return data.results;
    if (Array.isArray(data?.data)) return data.data;
    return [];
};

const getObjectName = (item) => {
    if (item === null || item === undefined) return "";

    if (typeof item === "object") {
        return (
            item.name ||
            item.division_name ||
            item.subject_name ||
            item.grade_class_name ||
            item.title ||
            ""
        );
    }

    return "";
};

const getObjectId = (item) => {
    if (item === null || item === undefined) return null;

    if (typeof item === "object") {
        return item.id ?? item.pk ?? item.value ?? null;
    }

    return item;
};

const getTeacherDivisionIds = (teacher) => {
    const values = [];

    if (Array.isArray(teacher?.divisions)) {
        values.push(...teacher.divisions);
    } else if (
        teacher?.divisions !== null &&
        teacher?.divisions !== undefined
    ) {
        values.push(teacher.divisions);
    }

    if (
        teacher?.division !== null &&
        teacher?.division !== undefined
    ) {
        values.push(teacher.division);
    }

    if (
        teacher?.division_id !== null &&
        teacher?.division_id !== undefined
    ) {
        values.push(teacher.division_id);
    }

    return values
        .map(getObjectId)
        .filter((id) => id !== null && id !== undefined)
        .map(String);
};

const TeacherBioDataDivision = () => {
    const { selectedYear } = useAcademicYear();
    const [messageApi, contextHolder] = message.useMessage();

    const [divisions, setDivisions] = useState([]);
    const [teachers, setTeachers] = useState([]);
    const [subjects, setSubjects] = useState([]);
    const [gradeClasses, setGradeClasses] = useState([]);

    const [selectedDivision, setSelectedDivision] = useState(undefined);
    const [loading, setLoading] = useState(false);
    const [downloading, setDownloading] = useState(false);

    const selectedDivisionObject = useMemo(
        () =>
            divisions.find(
                (division) =>
                    String(division.id) === String(selectedDivision)
            ),
        [divisions, selectedDivision]
    );

    const selectedDivisionName =
        selectedDivisionObject?.name ||
        selectedDivisionObject?.division_name ||
        selectedDivisionObject?.title ||
        "Selected Division";

    const fetchAllTeachers = useCallback(async () => {
        if (!selectedYear?.id) return [];

        let firstRequest = true;
        let nextUrl = null;
        const allTeachers = [];
        const visitedUrls = new Set();

        while (firstRequest || nextUrl) {
            let response;

            if (firstRequest) {
                response = await api.get("/api/users/teacher-data/", {
                    params: {
                        academic_year: selectedYear.id,
                    },
                });
                firstRequest = false;
            } else {
                if (visitedUrls.has(nextUrl)) break;
                visitedUrls.add(nextUrl);
                response = await api.get(nextUrl);
            }

            const data = response?.data;
            const pageRecords = getArray(data);

            allTeachers.push(...pageRecords);

            nextUrl =
                !Array.isArray(data) && data?.next
                    ? data.next
                    : null;

            if (Array.isArray(data)) break;
        }

        return allTeachers;
    }, [selectedYear?.id]);

    const loadData = useCallback(async () => {
        if (!selectedYear?.id) {
            setTeachers([]);
            setDivisions([]);
            setSubjects([]);
            setGradeClasses([]);
            return;
        }

        try {
            setLoading(true);

            const [
                divisionResponse,
                teacherRecords,
                subjectResponse,
                gradeResponse,
            ] = await Promise.all([
                api.get("/api/users/divisions/"),
                fetchAllTeachers(),
                api.get("/api/users/subjects/"),
                api.get("/api/users/grade-classes/"),
            ]);

            setDivisions(getArray(divisionResponse.data));
            setTeachers(Array.isArray(teacherRecords) ? teacherRecords : []);
            setSubjects(getArray(subjectResponse.data));
            setGradeClasses(getArray(gradeResponse.data));
        } catch (error) {
            console.error("Teacher division biodata loading error:", error);
            messageApi.error(
                "Unable to load teacher and division records."
            );
        } finally {
            setLoading(false);
        }
    }, [selectedYear?.id, fetchAllTeachers, messageApi]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const divisionOptions = useMemo(
        () =>
            divisions.map((division) => ({
                value: String(division.id),
                label:
                    division.name ||
                    division.division_name ||
                    division.title ||
                    `Division ${division.id}`,
            })),
        [divisions]
    );

    const filteredTeachers = useMemo(() => {
        if (!selectedDivision) return [];

        return teachers.filter((teacher) =>
            getTeacherDivisionIds(teacher).includes(
                String(selectedDivision)
            )
        );
    }, [teachers, selectedDivision]);

    const getSubjectNames = useCallback(
        (teacher) => {
            if (Array.isArray(teacher?.subject_names)) {
                return teacher.subject_names.join(", ") || "None";
            }

            if (Array.isArray(teacher?.subjects_display)) {
                return teacher.subjects_display.join(", ") || "None";
            }

            if (typeof teacher?.subjects_display === "string") {
                return teacher.subjects_display || "None";
            }

            if (!Array.isArray(teacher?.subjects)) {
                return getObjectName(teacher?.subjects) || "None";
            }

            const names = teacher.subjects
                .map((item) => {
                    const objectName = getObjectName(item);
                    if (objectName) return objectName;

                    const id = getObjectId(item);
                    const subject = subjects.find(
                        (entry) => String(entry.id) === String(id)
                    );

                    return (
                        subject?.name ||
                        subject?.subject_name ||
                        subject?.title ||
                        ""
                    );
                })
                .filter(Boolean);

            return names.join(", ") || "None";
        },
        [subjects]
    );

    const getGradeNames = useCallback(
        (teacher) => {
            if (Array.isArray(teacher?.grade_class_names)) {
                return teacher.grade_class_names.join(", ") || "None";
            }

            if (Array.isArray(teacher?.grade_classes_display)) {
                return teacher.grade_classes_display.join(", ") || "None";
            }

            if (typeof teacher?.grade_classes_display === "string") {
                return teacher.grade_classes_display || "None";
            }

            const assignedClasses =
                teacher?.grade_class ??
                teacher?.grade_classes ??
                [];

            if (!Array.isArray(assignedClasses)) {
                const objectName = getObjectName(assignedClasses);
                if (objectName) return objectName;

                const id = getObjectId(assignedClasses);
                const grade = gradeClasses.find(
                    (entry) => String(entry.id) === String(id)
                );

                return (
                    grade?.name ||
                    grade?.grade_class_name ||
                    grade?.class_name ||
                    "None"
                );
            }

            const names = assignedClasses
                .map((item) => {
                    const objectName = getObjectName(item);
                    if (objectName) return objectName;

                    const id = getObjectId(item);
                    const grade = gradeClasses.find(
                        (entry) => String(entry.id) === String(id)
                    );

                    return (
                        grade?.name ||
                        grade?.grade_class_name ||
                        grade?.class_name ||
                        ""
                    );
                })
                .filter(Boolean);

            return names.join(", ") || "None";
        },
        [gradeClasses]
    );

    const prepareTeacherData = useCallback(
        (teacher, index) => ({
            ...teacher,
            serial_number: index + 1,
            subjects_display: getSubjectNames(teacher),
            grade_classes_display: getGradeNames(teacher),
        }),
        [getSubjectNames, getGradeNames]
    );

const handleDownloadPDF = async () => {
    if (!selectedDivision) {
        messageApi.warning("Please select a division first.");
        return;
    }

    if (!selectedDivisionObject) {
        messageApi.error(
            "The selected division could not be found. Please refresh and try again."
        );
        return;
    }

    if (filteredTeachers.length === 0) {
        messageApi.warning(
            "There are no teacher records assigned to this division."
        );
        return;
    }

    try {
        setDownloading(true);

        const preparedTeachers = filteredTeachers.map(
            (teacher, index) =>
                prepareTeacherData(teacher, index)
        );

        // Resolve the actual division name, never use its ID.
        const actualDivisionName =
            selectedDivisionObject.name ||
            selectedDivisionObject.division_name ||
            selectedDivisionObject.title ||
            "Selected Division";

        // IMPORTANT: Do not name this variable "document".
        const pdfDocument = (
            <TeacherBioDataDivisionPDF
                teachers={preparedTeachers}
                divisionName={actualDivisionName}
                academicYear={selectedYear?.name || "N/A"}
            />
        );

        // Generate PDF.
        const blob = await pdf(pdfDocument).toBlob();

        if (!blob || blob.size === 0) {
            throw new Error("The generated PDF is empty.");
        }

        // Create download URL.
        const url = URL.createObjectURL(blob);

        // Use the browser's actual document object.
        const link = window.document.createElement("a");

        link.href = url;
        link.download = `Teacher_Biodata_${actualDivisionName.replace(
            /[^a-z0-9]/gi,
            "_"
        )}_${(selectedYear?.name || "Academic_Year").replace(
            /[^a-z0-9]/gi,
            "_"
        )}.pdf`;

        window.document.body.appendChild(link);
        link.click();
        window.document.body.removeChild(link);

        // Release the URL after initiating the download.
        setTimeout(() => URL.revokeObjectURL(url), 1000);

        messageApi.success(
            "Teacher biodata PDF downloaded successfully."
        );

    } catch (error) {
        console.error("Teacher biodata PDF generation error:", error);

        messageApi.error(
            error?.message
                ? `PDF generation failed: ${error.message}`
                : "Failed to generate the teacher biodata PDF."
        );
    } finally {
        setDownloading(false);
    }
};

    return (
        <>
            {contextHolder}

            <div
                style={{
                    padding: 20,
                    minHeight: "100%",
                    background: "#f5f7fa",
                }}
            >
                <Card
                    bordered={false}
                    style={{
                        borderRadius: 12,
                        boxShadow: "0 3px 14px rgba(9,45,80,0.07)",
                        marginBottom: 18,
                    }}
                >
                    <Row
                        align="middle"
                        justify="space-between"
                        gutter={[16, 16]}
                    >
                        <Col>
                            <Title
                                level={4}
                                style={{
                                    color: "#092D50",
                                    margin: 0,
                                }}
                            >
                                <TeamOutlined style={{ marginRight: 9 }} />
                                Teacher Biodata by Division
                            </Title>

                            <Text type="secondary">
                                Generate a complete teacher information
                                register for a selected division.
                            </Text>
                        </Col>

                        <Col>
                            <Button
                                icon={<ReloadOutlined />}
                                onClick={loadData}
                                loading={loading}
                            >
                                Refresh
                            </Button>
                        </Col>
                    </Row>
                </Card>

                <Row gutter={[16, 16]} style={{ marginBottom: 18 }}>
                    <Col xs={24} sm={12} md={8}>
                        <Card bordered={false} style={{ borderRadius: 10 }}>
                            <Statistic
                                title="Academic Year"
                                value={selectedYear?.name || "N/A"}
                                valueStyle={{
                                    fontSize: 19,
                                    color: "#092D50",
                                }}
                            />
                        </Card>
                    </Col>

                    <Col xs={24} sm={12} md={8}>
                        <Card bordered={false} style={{ borderRadius: 10 }}>
                            <Statistic
                                title="Available Divisions"
                                value={divisions.length}
                                prefix={<ApartmentOutlined />}
                                valueStyle={{ color: "#174F7D" }}
                            />
                        </Card>
                    </Col>

                    <Col xs={24} sm={12} md={8}>
                        <Card bordered={false} style={{ borderRadius: 10 }}>
                            <Statistic
                                title="Total Teacher Records"
                                value={teachers.length}
                                prefix={<TeamOutlined />}
                                valueStyle={{ color: "#267342" }}
                            />
                        </Card>
                    </Col>
                </Row>

                <Card
                    bordered={false}
                    style={{
                        borderRadius: 12,
                        boxShadow: "0 3px 14px rgba(9,45,80,0.06)",
                    }}
                >
                    <Title
                        level={5}
                        style={{ color: "#092D50", marginTop: 0 }}
                    >
                        Select Division
                    </Title>

                    <Row gutter={[12, 12]} align="middle">
                        <Col xs={24} md={14} lg={16}>
                            <Select
                                showSearch
                                allowClear
                                placeholder="Choose a division"
                                optionFilterProp="label"
                                value={selectedDivision}
                                onChange={(value) =>
                                    setSelectedDivision(value)
                                }
                                options={divisionOptions}
                                loading={loading}
                                style={{ width: "100%" }}
                                size="large"
                            />
                        </Col>

                        <Col xs={24} md={10} lg={8}>
                            <Button
                                type="primary"
                                icon={<FilePdfOutlined />}
                                onClick={handleDownloadPDF}
                                loading={downloading}
                                disabled={!selectedDivision || loading}
                                size="large"
                                block
                                style={{
                                    background: "#092D50",
                                    borderColor: "#092D50",
                                    fontWeight: 600,
                                }}
                            >
                                <DownloadOutlined />
                                Download PDF
                            </Button>
                        </Col>
                    </Row>

                    <div
                        style={{
                            marginTop: 18,
                            padding: 14,
                            borderRadius: 8,
                            background: "#F3F6F8",
                            border: "1px solid #E1E8EF",
                        }}
                    >
                        <Row
                            align="middle"
                            justify="space-between"
                            gutter={[10, 10]}
                        >
                            <Col>
                                <Text type="secondary">
                                    Selected division
                                </Text>
                                <div
                                    style={{
                                        color: "#092D50",
                                        fontWeight: 700,
                                        marginTop: 3,
                                    }}
                                >
                                    {selectedDivision
                                        ? selectedDivisionName
                                        : "No division selected"}
                                </div>
                            </Col>

                            <Col>
                                <Tag color="blue">
                                    {filteredTeachers.length} teacher
                                    {filteredTeachers.length === 1 ? "" : "s"}
                                </Tag>
                            </Col>
                        </Row>
                    </div>

                    <div style={{ marginTop: 18 }}>
                        {loading ? (
                            <div
                                style={{
                                    padding: 35,
                                    textAlign: "center",
                                }}
                            >
                                <Spin size="large" />
                                <div style={{ marginTop: 12 }}>
                                    <Text type="secondary">
                                        Loading teacher records...
                                    </Text>
                                </div>
                            </div>
                        ) : !selectedDivision ? (
                            <Empty
                                description="Select a division to view its teacher count and generate the PDF."
                            />
                        ) : filteredTeachers.length === 0 ? (
                            <Empty
                                description="No teachers are assigned to this division."
                            />
                        ) : (
                            <div
                                style={{
                                    padding: 14,
                                    border: "1px solid #D5DEE6",
                                    borderRadius: 8,
                                    background: "#fff",
                                }}
                            >
                                <Text strong style={{ color: "#092D50" }}>
                                    Ready to generate report
                                </Text>
                                <div style={{ marginTop: 5 }}>
                                    <Text type="secondary">
                                        The PDF will include all{" "}
                                        <strong>
                                            {filteredTeachers.length}
                                        </strong>{" "}
                                        teacher records assigned to{" "}
                                        <strong>{selectedDivisionName}</strong>.
                                        The report will continue onto
                                        additional pages automatically when
                                        necessary.
                                    </Text>
                                </div>
                            </div>
                        )}
                    </div>
                </Card>
            </div>
        </>
    );
};

export default TeacherBioDataDivision;